import $ from "jquery";
import _ from "underscore";
import async from "async";
import Operation from "core/models/Operation";
import OperationSet from "core/models/OperationSet";
import ServiceSelector from "app/views/map/ServiceSelector";
import ObjectLayerSelector from "app/views/map/ObjectLayerSelector";
import FormView from "core/views/FormView";
import GeoUtils from "app/utils/GeoUtils";
import SymbolFactory from "app/models/map/SymbolFactory";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import MapPrintTask from "app/models/map/MapPrintTask";
import CSVView from "core/views/CSVView";
import CollectionTableView from "core/views/CollectionTableView";
import Layer from "app/models/Layer";
import Geometry from "app/models/map/Geometry";
import xlsx from "xlsx";
import { saveAs } from "file-saver";
import ButtonView from "core/views/ButtonView";
import ErrorUtils from "app/utils/ErrorUtils";
import ModalView from "core/views/ModalView";
import Page from "core/Page";
import Enums from "app/enums";
import EditableLayerBase from "app/models/map/EditableLayerBase";
import Backbone from "backbone";
import _cloneDeep from "lodash.clonedeep";
import session from "app/Session";
import clipboard from "core/Clipboard";
import WindowView from "core/views/WindowView";
import modals from "core/views/modals";
import "../../../../styles/specific/batch-attribute-update.scss";
import isMobile from "core/is-mobile";
import messenger from "app/Messenger";
import mapContext from "app/views/map/MapContext";
import BaseRouter from "core/base/Router";
import FeatureResults from "app/models/map/FeatureResults";
import Feature from "app/models/Feature";

function hasModels(model) {
  return model && !!model.length;
}

function hasSingleModel(model) {
  return model && model.length == 1;
}

function hasEditableFeatures(model) {
  model = normalizeModels(model);
  return (
    model &&
    model.length &&
    model.every(function (model) {
      return (
        !model.isExternal() &&
        (model.isNew() || model.getFeature().get("canUpdate"))
      );
    })
  );
}

function finishSpatialQuery(err, results, deferred) {
  if (err) {
    if (_.isBoolean(err)) {
      deferred.reject(false);
    } else {
      deferred.reject(err);
    }
  } else {
    mapContext.setSelection(results);
    mapContext
      .getOperation("show-geometry-details")
      .action(results)
      .then(
        function (model) {
          deferred.resolve(model);
        },
        function (err) {
          deferred.reject(err);
        }
      );
  }
}

//todo think about moving this and getLayerForSave to MapContext to make testing less weird
function getServiceForSave(cb) {
  var internalServiceLayers = _.filter(
    mapContext.getConfiguration().serviceLayers,
    function (serviceLayer) {
      return serviceLayer.service && !serviceLayer.service.get("external");
    }
  );
  var internalServices = _.map(internalServiceLayers, function (serviceLayer) {
    return serviceLayer.service;
  });

  ServiceSelector.selectService(
    internalServiceLayers,
    internalServices,
    {
      create: function (service) {
        mapContext.getConfiguration().addService(service);
      },
      canCreate: session.can("create", "MapService") && !isMobile,
    },
    cb
  );
}

function getLayerForSave(serviceLayer, cb) {
  ObjectLayerSelector.selectLayer(
    serviceLayer,
    {
      create: function (serviceLayer, layer) {
        serviceLayer.addLayer(layer);
      },
      filter: function (layer) {
        return layer.allows(session, "add");
      },
      canCreate: Layer.allowCreate(session, serviceLayer.service) && !isMobile,
      noUpload: isMobile,
    },
    cb
  );
}

function getBufferDistance(cb) {
  var values = new Backbone.Model({
    distance: 5,
  });
  var fields = [
    new Field({
      id: "distance",
      name: "distance",
      label: "Distance in feet",
      editor: "number",
    }),
  ];
  var iView = new FormInputView({
    model: values,
    field: fields[0],
  });

  iView.render();

  modals.confirm(
    "Query Entry",
    iView.$el,
    {
      type: "primary",
      text: "Done",
    },
    function () {
      var distance = iView.options.model.get("distance") * 1;

      if (_.isNumber(distance) && !_.isNaN(distance)) {
        cb(null, distance);
      } else {
        cb("Invalid distance entry.");
      }
    },
    "Cancel",
    function () {
      cb(true);
    }
  );
}

/**
 * Gets the list of fields that are available for the given models, in addition to a field for locating the model and a field for selecting the layer.
 * @param  {FeatureResults} models Collection to consider.
 * @return {Array} Array of Field instances representing the available fields.
 */
function getExportFields(models) {
  var result = models.getExportFields();

  result.push(createSelectField());

  return result;
}

function createSelectField() {
  return new Field({
    id: "select",
    name: "select",
    label: "Select",
    editable: false,
    editor: "custom",
    ViewerView: {
      tagName: "a",
      className: "action-view action-map-marker",
      attributes: {
        href: "#",
      },

      template: _.template(
        '<span class="glyphicon glyphicon-<%= icon %>"></span>'
      ),

      events: {
        click: function (evt) {
          evt.preventDefault();

          var model = this.options.model;
          var selection = [model];

          mapContext
            .getOperation("pan-to-geometry")
            .action(selection)
            .then(function () {
              return mapContext
                .getOperation("highlight-geometry")
                .action(selection)
                .then(function () {
                  mapContext.setSelection(selection);
                  return mapContext
                    .getOperation("show-geometry-details")
                    .action(selection);
                });
            })
            .fail(
              ErrorUtils.errorHandler({
                activity: "selecting the geometry",
              })
            );
        },
      },

      serialize: function () {
        return {
          icon: "map-marker",
        };
      },
    },
  });
}

/**
 * Converts all given models to features. Uses the `originalLayer` field to determine the layer for each item.
 * @param  {Array|FeatureResults} models The array of data or geometry to normalize.
 * @return {FeatureResults} The collection of normalized features.
 */
function normalizeModels(models) {
  var normalized = models;

  if (!(models instanceof FeatureResults)) {
    normalized = new FeatureResults(models);
  }

  return normalized;
}

function saveModel(model, cb) {
  cb = _.once(cb);

  model.save(null, {
    success: function () {
      cb();
    },
    error: function (model, response) {
      cb(ErrorUtils.normalizeError({}, response));
    },
  });
}

export default new OperationSet([
  new Operation({
    id: "copy",
    text: "Copy",
    icon: "copy",
    action: function (model) {
      var results = normalizeModels(model);
      var geometries = results.toGeometry();

      if (model.length == 1) {
        clipboard.put("properties", results.at(0).getFeature().getProperties());
      }

      clipboard.put("geometry", geometries);

      return Operation.resolve(model);
    },
    available: function (model) {
      return model && model.length;
    },
  }),

  new Operation({
    id: "paste",
    text: "Paste",
    icon: "paste",
    action: function (model) {
      var geometry = clipboard.get("geometry");

      if (!_.isArray(geometry)) {
        geometry = [];
      }

      _.each(geometry, function (item) {
        mapContext.getConfiguration().getDrawingLayer().add(item.clone());
      });

      return Operation.resolve(model);
    },
    available: function () {
      return clipboard.has("geometry");
    },
  }),

  new Operation({
    id: "paste-data",
    text: "Paste Data",
    icon: "paste",
    action: function (models) {
      var deferred = new $.Deferred();

      models = normalizeModels(models);

      var model = models.first();

      modals.confirm(
        "Delete Object",
        "Are you sure you want to paste data into this object? This will permanently overwrite all properties with the copied values.",
        {
          text: "Overwrite",
          type: "danger",
        },
        function () {
          var data = clipboard.get("properties");
          var featureData = model.getFeature().getProperties();

          featureData.set(data.attributes);

          deferred.resolve(model, "Data pasted successfully");
        },
        "Cancel",
        function () {
          deferred.reject(false);
        }
      );

      return deferred;
    },
    available: function (models) {
      return clipboard.has("properties") && hasSingleModel(models);
    },
  }),

  new Operation({
    id: "remove-geometry",
    text: "Remove",
    icon: "trash",
    action: function (model) {
      model = normalizeModels(model);

      var deferred = new $.Deferred();
      var directRemove = model.every(function (model) {
        return model.isNew();
      }); //if all we have are new models, just kill them

      var plural = model.length == 1 ? "" : "s";

      if (model.length > 0) {
        if (directRemove) {
          model.each(function (model) {
            //model.originalLayer.remove(model.originalLayer.geometryForData(model));
            model.originalLayer.remove(model);
          });
          deferred.resolve(model, "Object" + plural + " removed successfully");
        } else {
          modals
            .confirm(
              "Delete Object",
              "Are you sure you want to delete " +
                (plural ? "the selected objects?" : "this object?"),
              {
                text: "Delete",
                type: "danger",
                className: isMobile ? "btn-lg" : "",
              },
              function () {
                async.each(
                  model.models.slice(),
                  function (model, cb) {
                    var layer = model.originalLayer;
                    var feature = layer.toFeature(model);

                    //should remove it from this view's internal collection
                    feature.destroy({
                      success: function () {
                        layer.remove(model);
                        cb();
                      },
                      error: function (model, response) {
                        cb(
                          ErrorUtils.normalizeError(
                            {
                              activity: "removing feature data",
                            },
                            response
                          )
                        );
                      },
                    });
                  },
                  function (err) {
                    if (err) {
                      deferred.reject(err);
                    } else {
                      deferred.resolve(
                        model,
                        "Object" + plural + " removed successfully"
                      );
                    }
                  }
                );
              },
              {
                text: "Cancel",
                className: isMobile ? "btn-lg" : "",
              },
              function () {
                deferred.reject(false);
              }
            )
            .$el.addClass("delete-object");
        }

        return deferred.promise();
      } else {
        deferred.resolve(model, "No model selected");

        return deferred.promise();
      }
    },
    available: function (model) {
      model = normalizeModels(model);

      return (
        model &&
        model.length &&
        model.every(function (model) {
          return (
            !model.isExternal() && (model.isNew() || model.get("canDelete"))
          );
        })
      );
    },
  }),

  new Operation({
    id: "batch-update-geometry",
    text: "Set Attributes",
    action: function (model) {
      var models = normalizeModels(model);
      var fields = models.getSharedFields();
      var deferred = new $.Deferred();
      var title = "Save Object";
      var saveButton = "Save";
      var editModel = models.first().originalLayer.toData(models.first()); //we don't need to check array size here because it should've been checked in the `available` routine

      if (models.length > 1) {
        title = "Batch Attribute Update";
        saveButton = "Update Attributes";
        editModel = {};
      }

      var formView = new FormView({
        model: editModel,
        fields: fields,
      });

      formView.render();

      if (models.length > 1) {
        formView.$el.prepend(
          "<p>Set specific field values across the entire selection</p></br>"
        );
        _.each(formView.inputViews, function (inputView) {
          inputView.$el.addClass("col-sm-6");
        });
        formView.$el.append('<div class="clearfix"></div>');
      }

      function error(err) {
        deferred.reject(
          ErrorUtils.normalizeError(
            {
              activity: "updating geometry data",
            },
            err
          )
        );
      }

      function continueSave() {
        if (models.length == 1) {
          saveModel(models.at(0), function (err) {
            if (err) {
              error(err);
            } else {
              deferred.resolve(model);
            }
          });
        } else {
          async.each(
            models.models,
            function (model, cb) {
              var dataModel = model.originalLayer.toData(model);

              _.each(formView.model.attributes, function (value, fieldName) {
                dataModel.set(fieldName, value);
              });

              saveModel(model, cb);
            },
            function (err) {
              if (err) {
                error(err);
              } else {
                deferred.resolve(model);
              }
            }
          );
        }
      }

      modals.confirm(
        title,
        formView.$el,
        {
          text: saveButton,
          type: "primary",
        },
        function () {
          formView.propagate().done(continueSave).fail(error);
        },
        "Cancel",
        function () {
          deferred.reject(false);
        }
      );

      return deferred.promise();
    },
    available: function (model) {
      model = normalizeModels(model);

      return (
        model &&
        model.length &&
        model.every(function (model) {
          return (
            !model.isExternal() && (model.isNew() || model.get("canUpdate"))
          );
        })
      );
    },
  }),

  new Operation({
    id: "move-geometry",
    text: "Move",
    icon: "share-alt",
    action: function (model) {
      //first, prompt the user to either create a service or use an existing service
      //second, using that service, prompt the user to either create a layer (ObjectLayer) or use an existing layer
      //third, using the fields from that layer, allow the user to set field values for the object before finally adding it to the layer
      var deferred = new $.Deferred();
      var singular = model.length == 1;
      var successMessage = singular
        ? "Feature saved successfully."
        : "Features saved successfully.";

      model = normalizeModels(model);

      async.waterfall(
        [
          function (cb) {
            getServiceForSave(cb);
          },
          function (serviceLayer, cb) {
            getLayerForSave(serviceLayer, cb);
          },
          function (destinationLayer, cb) {
            /*
             * todo: try to do this in parallel
             * the problem right now is that if we try it in parallel with multiple geometries, and we get an error
             * on a save, then we only revert the object with the error, while the other objects might get "stuck".
             * In other words, if an error occurs in parallel right now, we end up in an inconsistent state.
             * The ideal solution would involve some form of queue as well as some handling of multiple possible
             * error messages.
             */
            async.eachSeries(
              model.models,
              function (model, cb) {
                var sourceLayer = model.originalLayer;
                var geometry = sourceLayer.toGeometry(model);

                EditableLayerBase.moveModel(
                  geometry,
                  sourceLayer,
                  destinationLayer,
                  function (cb) {
                    if (singular) {
                      var operation = mapContext.operations.get(
                        "batch-update-geometry"
                      );

                      if (operation.available([geometry])) {
                        operation
                          .action([geometry])
                          .done(function () {
                            cb();
                          })
                          .fail(function (err) {
                            cb(err);
                          });
                      } else {
                        cb(); //if there aren't any fields, we might as well just save it
                      }
                    } else {
                      cb();
                    }
                  },
                  function (err) {
                    if (err) {
                      return cb(
                        ErrorUtils.normalizeError(
                          {
                            activity: "moving the geometry",
                          },
                          err
                        )
                      );
                    }

                    cb();
                  }
                );
              },
              function (err) {
                if (err) {
                  return cb(err);
                }

                return cb(null, model);
              }
            );
          },
        ],
        function (err, newModel) {
          if (err) {
            if (_.isBoolean(err)) {
              return deferred.reject(false);
            } else {
              return deferred.reject(err);
            }
          }

          deferred.resolve(newModel, successMessage);
        }
      );

      return deferred.promise();
    },
    available: hasEditableFeatures,
  }),

  new Operation({
    id: "commit-geometry",
    text: "Save",
    icon: "floppy-disk",
    action: function (model) {
      var deferred = new $.Deferred();
      var singular = model.length == 1;

      model = normalizeModels(model);

      async.each(model.models, saveModel, function (err) {
        if (err) {
          if (_.isBoolean(err)) {
            return deferred.reject(false);
          } else {
            return deferred.reject(err);
          }
        }

        deferred.resolve(
          model,
          singular
            ? "Object saved successfully."
            : "Objects saved successfully."
        );
      });

      return deferred.promise();
    },

    available: hasEditableFeatures,
  }),

  new Operation({
    id: "save-geometry",
    text: "Save",
    icon: "floppy-disk",
    action: function (model) {
      var deferred = new $.Deferred();
      var singular = model.length == 1;
      var wasNew = false;

      model = normalizeModels(model);

      async.each(
        model.models,
        function (model, cb) {
          var geometry = model;
          var layer = model.originalLayer;
          var modelReal = layer.toData(geometry);

          function error(err) {
            if (err) {
              cb(
                ErrorUtils.normalizeError(
                  {
                    activity: "saving the geometry",
                  },
                  err
                )
              );
            } else {
              cb(true);
            }
          }

          function continueSave() {
            saveModel(geometry, cb);
          }

          if (modelReal.isNew()) {
            wasNew = true;
          }

          if (singular) {
            var formView = new FormView({
              model: modelReal,
              fields: layer.getFields(),
            });

            wasNew = true;

            formView.render();

            modals.confirm(
              "Save Object",
              formView.$el,
              {
                text: "Save",
                type: "primary",
              },
              function () {
                formView.propagate().done(continueSave).fail(error);
              },
              "Cancel",
              error
            );
          } else {
            continueSave();
          }
        },
        function (err) {
          if (err) {
            if (_.isBoolean(err)) {
              return deferred.reject(false);
            } else {
              return deferred.reject(err);
            }
          }

          if (wasNew) {
            mapContext.display(model);
          }

          deferred.resolve(
            model,
            singular
              ? "Object saved successfully."
              : "Objects saved successfully."
          );
        }
      );

      return deferred.promise();
    },
    available: hasEditableFeatures,
  }),

  new Operation({
    id: "zoom-to-geometry",
    text: "Zoom To Geometry",
    icon: "fullscreen",
    action: function (model) {
      if (!model || model.length === 0) {
        model = mapContext.getSelection();
      }

      model = normalizeModels(model);

      var deferred = new $.Deferred();

      var geometryExtent = model.getExtent();

      if (geometryExtent) {
        var expandedGeometryExtent =
          GeoUtils.expandExtentForView(geometryExtent);
        var defaultError = "Error occurred setting extent of map.";

        async.retry(
          { times: 5, interval: 500 },
          function (cb) {
            var promise = mapContext
              .getMap()
              .setExtent(expandedGeometryExtent, true);
            promise.then(function () {
              cb();
            });
            if (promise.fail) {
              promise.fail(function (err) {
                cb(err || defaultError);
              });
            } else if (promise.catch) {
              promise.catch(function (err) {
                cb(err || defaultError);
              });
            }
          },
          function (err) {
            if (err) {
              return deferred.reject(err);
            }

            deferred.resolve(model);
          }
        );
      } else {
        deferred.resolve(model);
      }

      return deferred.promise();
    },
    available: function (model) {
      return model && model.length;
    },
  }),

  new Operation({
    id: "pan-to-geometry",
    text: "Pan To Geometry",
    icon: "map-marker",
    action: function (model) {
      model = normalizeModels(model);

      var deferred = new $.Deferred();

      mapContext
        .getMap()
        .centerAt(GeoUtils.getCenter(model.getExtent()))
        .then(
          function () {
            deferred.resolve(model);
          },
          function (err) {
            deferred.reject(err);
          }
        );

      return deferred.promise();
    },
    available: function (model) {
      return model && model.length;
    },
  }),

  new Operation({
    id: "highlight-geometry",
    text: "Highlight",
    icon: "",
    action: function (model) {
      var toHighlight = {};
      var featureLayers = {};
      var deferred = new $.Deferred();
      var completion = $.when();

      model = normalizeModels(model);

      mapContext
        .getOperation("clear-selection")
        .action()
        .then(
          function () {
            model.each(function (feature) {
              var layer = feature.originalLayer;

              if (layer) {
                if (!toHighlight[layer.layer.id]) {
                  toHighlight[layer.layer.id] = [];
                }

                featureLayers[layer.layer.id] = layer;

                toHighlight[layer.layer.id].push(feature);
              }
            });

            _.each(toHighlight, function (featuresToHighlight, layerId) {
              completion = $.when(
                completion,
                featureLayers[layerId].highlight(
                  SymbolFactory.highlight(),
                  featuresToHighlight
                )
              );
            });

            completion.then(
              function () {
                deferred.resolve(model);
              },
              function (err) {
                deferred.reject(err);
              }
            );
          },
          function (err) {
            deferred.reject(err);
          }
        );

      return deferred.promise();
    },
    available: function (model) {
      return model && model.length;
    },
  }),

  new Operation({
    id: "show-geometry-details",
    text: "Show Details",
    icon: "info-sign",
    action: function (model) {
      var models = normalizeModels(model);

      mapContext.setSelection(models);
      mapContext.display(models);

      return Operation.resolve(model);
    },
    available: function (model) {
      return model && model.length;
    },
  }),

  new Operation({
    id: "run-query",
    text: "Run Spatial Query",
    icon: "question-sign",
    action: function (model) {
      var Query = window.require("esri/tasks/query");
      var results = normalizeModels(model);
      var deferred = new $.Deferred();
      var layers = mapContext.getConfiguration().getSelectedLayers();

      mapContext.setSearchRegion(results);

      results.executeQuery(
        layers,
        function (geometry, query) {
          var deferred = new $.Deferred();

          query.spatial(geometry, Query.SPATIAL_REL_INTERSECTS);

          deferred.resolve(query);

          return deferred.promise();
        },
        function (err, results) {
          return finishSpatialQuery(err, results, deferred);
        }
      );

      return deferred.promise();
    },
    available: hasSingleModel,
  }),

  new Operation({
    id: "run-buffer-query",
    text: "Run Buffered Spatial Query",
    icon: "play",
    action: function (model) {
      var Query = window.require("esri/tasks/query");
      var results = normalizeModels(model);
      var deferred = new $.Deferred();
      var layers = mapContext.getConfiguration().getSelectedLayers();

      mapContext.setSearchRegion(results);

      results.executeQuery(
        layers,
        function (geometry, query) {
          var deferred = new $.Deferred();

          getBufferDistance(function (err, distance) {
            if (err) {
              if (err === true) {
                return deferred.reject(false);
              } else {
                return deferred.reject(err);
              }
            }

            query.boundary(geometry, distance, Query.SPATIAL_REL_INTERSECTS);
            query.reference(mapContext.getMap().spatialReference);

            deferred.resolve(query);
          });

          return deferred.promise();
        },
        function (err, results) {
          return finishSpatialQuery(err, results, deferred);
        }
      );

      return deferred.promise();
    },
    available: hasSingleModel,
  }),

  new Operation({
    id: "print-geometry",
    text: "Print",
    icon: "print",
    action: function (model) {
      var deferred = new $.Deferred();
      var modalContent;
      var region = mapContext.getSearchRegion();

      model = normalizeModels(model);

      if (model.length > 1) {
        if (region.length) {
          var promptContent =
            "<span>We noticed that you decided to print all " +
            model.length +
            " objects in your selection area. Do you want to print all of these objects or just print the selection area?</span>";
          var prompt = new ModalView({
            title: "Print All Objects",
            modalClass: "print-all-objects",
            locked: true,
            buttons: [
              {
                text: "Cancel",
                click: function () {
                  prompt.remove();
                  deferred.reject(false);
                },
              },
              {
                type: "warning",
                text: "Print All " + model.length + " Objects",
                click: function () {
                  prompt.remove();
                  printOut(model);
                },
              },
              {
                type: "primary",
                text: "Print Only Selection Object",
                click: function () {
                  prompt.remove();
                  printOut(region);
                },
              },
            ],
          });

          prompt.render();
          prompt.setContent(promptContent);
          prompt.show();
        } else {
          modals.confirm(
            "Print All Objects",
            "Are you sure you want to print all " +
              model.length +
              " objects in your selection?",
            {
              type: "primary",
              text: "Continue",
            },
            function () {
              printOut(model);
            },
            "Cancel",
            function () {
              deferred.reject(false);
            }
          );
        }
      } else {
        printOut(model);
      }

      function printOut(model) {
        if (model.length == 1) {
          // @todo: Make this text more user-friendly
          modalContent =
            '<span>We are currently generating an image from the selected shape.<br/>You may print it from the new window that appears.<br/><br/><strong class="print-progress"></strong></span>';
        } else {
          modalContent =
            '<span>We are currently generating images from the selected shapes.<br/>You may print them from the new windows that appear.<br/><br/><strong class="print-progress"></strong></span>';
        }

        modalContent = $(modalContent);

        var cancel = function () {};
        var printProgress = modalContent.find(".print-progress");
        //alert: function(title, content, button, next, locked, remove){
        //modal: function(title, content, locked){
        //var modal = app.modal('Generating Images', modalContent, true);
        var modal = modals.alert(
          "Generating Images",
          modalContent,
          "Cancel",
          function () {
            cancel();
          },
          true
        );
        var printCount = 1;

        modal.inProgress(true);

        async.eachSeries(
          model.models,
          function (currentModel, cb) {
            var layer = currentModel.originalLayer;
            var printTask = new MapPrintTask(mapContext.getMap(), currentModel);

            printProgress.append(
              "Generating image " +
                printCount +
                " of " +
                model.length +
                "... </br>"
            );

            if (layer.toGeometry(currentModel).type == "polygon") {
              var layerVisible;

              printTask.once("start", function () {
                layerVisible = layer.visible;
                layer.hide();
              });

              printTask.once("stop", function () {
                if (layerVisible) {
                  layer.show();
                }
              });
            }

            cancel = function () {
              printTask.cancel();
            };

            printTask
              .execute()
              .done(function (result) {
                printCount++;
                window.open(result.url); //hopefully removing the `var a =` doesn't hurt printing

                window.focus();
                cb();
              })
              .fail(function (err) {
                layer.show();
                cb(err);
              });
          },
          function (err) {
            if (err) {
              if (_.isBoolean(err)) {
                deferred.reject(false);
              } else {
                deferred.reject(err);
              }
            } else {
              deferred.resolve(model);
            }

            modal.remove();
          }
        );
      }

      return deferred.promise();
    },
    available: function (model) {
      return model && model.length;
    },
  }),

  new Operation({
    id: "export-geometry-csv",
    text: "Export to CSV",
    icon: "export",
    action: function (model) {
      var results = normalizeModels(model);
      var csvView = new CSVView({
        fields: results.getExportFields(),
        models: results.toData(),
      });

      csvView.render();
      var windowView = WindowView.create("Results CSV", true, {
        content: csvView.$el,
      });

      windowView.show();
      windowView.bringToFront();

      if (BaseRouter.current) {
        BaseRouter.current.once("leave", function () {
          windowView.remove();
        });
      }

      windowView.size(Page.getWidth() / 2, Page.getMainContentHeight() / 2);
      windowView.center();
      windowView.$el.addClass("results-window csv-window");

      return Operation.resolve(model);
    },
    available: hasModels,
  }),

  new Operation({
    id: "export-geometry-table",
    text: "Export to table",
    icon: "new-window",
    action: function (model) {
      var results = normalizeModels(model);
      var fields = getExportFields(results);
      var dataModel = results.toData();
      var editable = !results.anyExternal();

      var collectionView = new CollectionTableView({
        fields: fields,
        collection: dataModel,
        allowSave: editable,
        autoSave: editable,
        allowDelete: false,
        paginate: true,
        mode: editable ? "edit" : "view",
      });

      collectionView.render();

      collectionView.on(
        "row-commit",
        _.throttle(
          function (row) {
            mapContext
              .getOperation("commit-geometry")
              .action([row.model.originalLayer.toGeometry(row.model)])
              .fail(
                ErrorUtils.errorHandler({
                  activity: "saving the geometry",
                })
              );
          },
          1500,
          {
            leading: false,
          }
        )
      );

      var windowView = WindowView.create("Results", true, {
        content: collectionView.$el,
        footer: collectionView.pager.$el,
        shadow: true,
      });

      windowView.show();
      windowView.bringToFront();

      if (mapContext.getOperation("download-to-excel").available(model)) {
        var exportButton = new ButtonView({
          text: "Download as Excel",
          click: function () {
            mapContext
              .getOperation("download-to-excel")
              .action(model)
              .fail(
                ErrorUtils.errorHandler({
                  activity: "downloading to excel",
                })
              );
          },
        });

        exportButton.render();
        exportButton.$el.prependTo(windowView.header.getRightControlBar());
      }

      if (mapContext.getOperation("batch-update-geometry").available(model)) {
        var batchButton = new ButtonView({
          text: "Set Attributes",
          click: function () {
            mapContext
              .getOperation("batch-update-geometry")
              .action(model)
              .fail(
                ErrorUtils.errorHandler({
                  activity: "displaying batch update",
                })
              )
              .done(function () {
                collectionView.refreshCurrentPage();
              })
              .done(function () {
                messenger.success("Successfully updated selected records.");
              });
          },
        });
        batchButton.render();
        batchButton.$el.prependTo(windowView.header.getRightControlBar());
      }

      if (mapContext.getOperation("move-geometry").available(model)) {
        var moveButton = new ButtonView({
          text: "Move All",
          click: function () {
            modals.confirm(
              "Move All",
              "Are you sure you want to move " +
                model.length +
                " features to a different layer?",
              "Continue",
              function () {
                mapContext
                  .getOperation("move-geometry")
                  .action(model)
                  .fail(
                    ErrorUtils.errorHandler({
                      activity: "moving geometries",
                    })
                  )
                  .done(function () {
                    collectionView.refreshCurrentPage();
                  });
              },
              "Go Back"
            );
          },
        });
        moveButton.render();
        moveButton.$el.prependTo(windowView.header.getRightControlBar());
      }

      if (BaseRouter.current) {
        BaseRouter.current.once("leave", function () {
          windowView.off();
          windowView.remove();
        });
      }

      windowView.size(Page.getWidth() / 2, Page.getMainContentHeight() / 2);
      windowView.center();
      windowView.refresh();
      windowView.$el.addClass("results-window");

      windowView
        .on(
          "maximized",
          function () {
            this.trigger("results-window-maximized", windowView);
          },
          this
        )
        .on(
          "restored",
          function () {
            this.trigger("results-window-restored", windowView);
          },
          this
        );

      return Operation.resolve(model);
    },
    available: hasModels,
  }),

  new Operation({
    id: "download-to-excel",
    text: "Download to Excel",
    icon: "download",
    action: function (model) {
      var results = normalizeModels(model);
      var exportFields = results.getExportFields();
      var dataModel = results.toData();

      var Workbook = function () {
        if (!(this instanceof Workbook)) {
          return new xlsx.Workbook();
        }
        this.SheetNames = [];
        this.Sheets = {};
      };

      var s2ab = function (s) {
        var buf = new ArrayBuffer(s.length);
        var view = new Uint8Array(buf);
        var i;

        for (i = 0; i != s.length; ++i) {
          view[i] = s.charCodeAt(i) & 0xff;
        }

        return buf;
      };

      var wb = new Workbook(),
        ws = {},
        currentColumn = 0,
        currentRow = 0,
        range = {
          s: {
            c: 10000000,
            r: 10000000,
          },
          e: {
            c: 0,
            r: 0,
          },
        };

      /*function datenum(v, date1904) {
			 if(date1904) v+=1462;
			 var epoch = Date.parse(v);
			 return (epoch - new Date(Date.UTC(1899, 11, 30))) / (24 * 60 * 60 * 1000);
			 }

			 if(typeof cell.v === 'number') cell.t = 'n';
			 else if(typeof cell.v === 'boolean') cell.t = 'b';
			 else if(cell.v instanceof Date) {
			 cell.t = 'n'; cell.z = XLSX.SSF._table[14];
			 cell.v = datenum(cell.v);
			 }
			 else cell.t = 's';*/

      _.each(exportFields, function (field) {
        var cellRef = xlsx.utils.encode_cell({
          c: currentColumn,
          r: currentRow,
        });
        var cell = {
          v: field.get("label") || field.get("name") || "",
          t: "s",
        };

        if (range.s.r > currentRow) {
          range.s.r = currentRow;
        }

        if (range.s.c > currentColumn) {
          range.s.c = currentColumn;
        }

        if (range.e.r < currentRow) {
          range.e.r = currentRow;
        }

        if (range.e.c < currentColumn) {
          range.e.c = currentColumn;
        }

        ws[cellRef] = cell;
        currentColumn++;
      });

      currentColumn = 0;
      currentRow++;

      _.each(dataModel, function (item) {
        _.each(exportFields, function (field) {
          var cellRef = xlsx.utils.encode_cell({
            c: currentColumn,
            r: currentRow,
          });

          var cell = {
            t: "s",
          };
          var fieldValue = field.getValue(item);

          if (field.hasValues()) {
            fieldValue = _.result(
              _.find(field.get("values"), function (value) {
                return value.value == fieldValue;
              }),
              "text"
            );
          }

          cell.v = String(fieldValue);

          if (field.get("type") == Enums.FormQuestionType.Date) {
            fieldValue = new Date(fieldValue);
            cell.v =
              (fieldValue - new Date(Date.UTC(1899, 11, 30))) /
              (24 * 60 * 60 * 1000);
            cell.t = "n";
            cell.z = xlsx.SSF._table[14];
          } else if (
            field.get("type") == Enums.FormQuestionType.Numeric ||
            !_.isNaN(Number(fieldValue))
          ) {
            cell.v = fieldValue;
            cell.t = "n";
          }

          if (_.isUndefined(fieldValue)) {
            delete cell.v;
          }

          if (range.s.r > currentRow) {
            range.s.r = currentRow;
          }

          if (range.s.c > currentColumn) {
            range.s.c = currentColumn;
          }

          if (range.e.r < currentRow) {
            range.e.r = currentRow;
          }

          if (range.e.c < currentColumn) {
            range.e.c = currentColumn;
          }

          ws[cellRef] = cell;
          currentColumn++;
        });

        currentRow++;
        currentColumn = 0;
      });

      if (range.s.c < 10000000) {
        ws["!ref"] = xlsx.utils.encode_range(range);
      }

      /* add worksheet to workbook */
      wb.SheetNames.push("Export");
      wb.Sheets["Export"] = ws;

      var wbout = xlsx.write(wb, {
        bookType: "xlsx",
        bookSST: true,
        type: "binary",
      });

      saveAs(
        new Blob([s2ab(wbout)], {
          type: "application/octet-stream",
        }),
        "Export.xlsx"
      );

      return Operation.resolve(model, "Data exported successfully.");
    },
    available: function (model) {
      return !!Blob && model && !!model.length;
    },
  }),

  new Operation({
    id: "create-extent-geometry",
    text: "Create Extent Geometry",
    icon: "fullscreen",
    action: function (model) {
      var Point = window.require("esri/geometry/Point");
      var Polygon = window.require("esri/geometry/Polygon");
      model = normalizeModels(model);

      var extent = model.getExtent();

      var upperLeft = new Point(
        extent.xmin,
        extent.ymin,
        extent.spatialReference
      );
      var upperLeft2 = new Point(
        extent.xmin,
        extent.ymin,
        extent.spatialReference
      );
      var upperRight = new Point(
        extent.xmax,
        extent.ymin,
        extent.spatialReference
      );
      var lowerLeft = new Point(
        extent.xmin,
        extent.ymax,
        extent.spatialReference
      );
      var lowerRight = new Point(
        extent.xmax,
        extent.ymax,
        extent.spatialReference
      );

      var result = new Polygon(extent.spatialReference);
      //we want the order in the ring to be upperLeft thru lowerRight, so iterate on the reverse, since
      //insert will reverse the order to what we want
      result.addRing([
        upperLeft2,
        lowerLeft,
        lowerRight,
        upperRight,
        upperLeft,
      ]);
      //from this file mapContext.getConfiguration().serviceLayers,

      mapContext.getConfiguration().workingLayer.addGeometry(result);

      return Operation.resolve(model);
    },
    available: function (model) {
      return !!(model && model.length);
    },
  }),
  new Operation({
    id: "create-buffer-geometry",
    text: "Create Buffer Geometry",
    action: function (model) {
      var geometryEngine = window.require("esri/geometry/geometryEngine");
      var deferred = new $.Deferred();

      model = normalizeModels(model);

      var geometries = model.map(function (model) {
        return model.originalLayer.toGraphic(model).geometry;
      });

      getBufferDistance(function (err, value) {
        if (err === true) {
          deferred.reject(false);
        } else if (err) {
          deferred.reject(err);
        } else {
          var distance = [value];
          var FEET_UNIT = 9002;
          var DEVIATION = 1;

          _.each(geometries, function (geometry) {
            var result = geometryEngine.buffer(
              [geometry],
              distance,
              FEET_UNIT,
              true
            );

            result[0] = geometryEngine.generalize(result[0], DEVIATION);

            mapContext.getConfiguration().workingLayer.addGeometry(result[0]);
          });

          deferred.resolve(model);
        }
      });

      return deferred.promise();
    },
    available: function (model) {
      return !!(model && model.length);
    },
  }),

  new Operation({
    id: "revert-location-geometry",
    text: "Move Back",
    action: function (model) {
      var models = normalizeModels(model);
      var singular = model.length == 1;

      models.each(function (model) {
        var label = model.originalLayer.getLabelForModel(model);

        if (label._geometryOnLoad) {
          label.setGeometry(_cloneDeep(label._geometryOnLoad));
        }
      });

      mapContext.getConfiguration().workingLayer.layer.refresh();

      return Operation.resolve(
        model,
        singular ? "Object moved back." : "Objects moved back."
      );
    },
    available: hasEditableFeatures,
  }),

  new Operation({
    id: "join-geometry",
    text: "Join Features",
    action: function (models) {
      var deferred = new $.Deferred();
      var destinationFeature = null,
        destinationLayer = null;

      models = normalizeModels(models);

      //reversing here because, in the way selection happens, the last item in the selection list is the first item selected
      var coordinateLists = _.map(models.models.reverse(), function (model) {
        var feature = model.getFeature(),
          geometry = feature.getGeometry();

        if (
          !destinationFeature &&
          model.originalLayer != mapContext.getConfiguration().workingLayer
        ) {
          destinationFeature = feature;
          destinationLayer = model.originalLayer;
        }

        return geometry.get("coordinates");
      });

      function xhrError(cb, activity) {
        return function (xhr, textStatus, errorThrown) {
          cb(
            ErrorUtils.normalizeError(
              {
                activity: activity,
                textStatus: textStatus,
                errorThrown: errorThrown,
              },
              xhr
            )
          );
        };
      }

      function fail(err) {
        //add everything back in case an error occurs
        models.each(function (model) {
          var feature = model.getFeature();

          model.originalLayer.add(
            feature.getGeometry(),
            feature.getProperties()
          );
        });

        deferred.reject(err);
      }

      async.series(
        [
          // move documents to destination feature
          function (cb) {
            var destinationCollection = destinationFeature.getDocuments();

            async.each(
              models.models,
              function (model, cb) {
                var feature = model.getFeature();

                if (
                  feature == destinationFeature ||
                  model.originalLayer ==
                    mapContext.getConfiguration().workingLayer
                ) {
                  cb();
                } else {
                  var sourceCollection = feature.getDocuments();

                  sourceCollection
                    .fetch()
                    .fail(xhrError(cb, "retrieving documents to move"))
                    .done(function () {
                      _.each(sourceCollection.models, function (sourceModel) {
                        delete sourceModel.collection;
                      });

                      destinationCollection.add(sourceCollection.models);
                      cb();
                    });
                }
              },
              function (err) {
                if (err) {
                  return cb(err);
                }

                async.each(
                  destinationCollection.models,
                  function (model, cb) {
                    model
                      .save()
                      .fail(xhrError(cb, "moving document"))
                      .done(function () {
                        cb();
                      });
                  },
                  function (err) {
                    if (err) {
                      return cb(err);
                    }

                    cb();
                  }
                );
              }
            );
          },
          // join documents
          function (cb) {
            var unionGeometry;

            models.each(function (model) {
              model.originalLayer.remove(model.getFeature().getGeometry());
            });

            try {
              //ideally, we would use an ESRI library for this, but the geometryEngine wasn't working quite right
              //and was just throwing errors when trying to union lines together.
              unionGeometry = [];

              _.each(coordinateLists, function (coordinateList) {
                unionGeometry = unionGeometry.concat(coordinateList);
              });

              destinationFeature
                .getGeometry()
                .set("coordinates", unionGeometry);
              destinationLayer.add(
                destinationFeature.getGeometry(),
                destinationFeature.getProperties()
              );
            } catch (err) {
              cb(err);
            }

            cb();
          },
          // destroy other models and update the model everything has been joined to
          function (cb) {
            async.each(
              models.models,
              function (model, cb) {
                var feature = model.getFeature();
                var currentLayer = model.originalLayer,
                  workingLayer = mapContext.getConfiguration().workingLayer;

                if (currentLayer !== workingLayer) {
                  if (feature != destinationFeature) {
                    feature
                      .destroy()
                      .fail(
                        xhrError(
                          cb,
                          "deleting joined feature. Remaining features must be deleted manually"
                        )
                      )
                      .done(function () {
                        cb();
                      });
                  } else {
                    feature
                      .save()
                      .fail(xhrError(cb, "saving the resulting feature"))
                      .done(function () {
                        cb();
                      });
                  }
                } else {
                  cb();
                }
              },
              cb
            );
          },
        ],
        function (err) {
          if (err) {
            return fail(err);
          }

          deferred.resolve(
            [destinationFeature],
            "Features joined successfully."
          );
        }
      );

      return deferred.promise();
    },

    available: function (models) {
      models = normalizeModels(models);
      if (hasEditableFeatures(models) && models.length > 1) {
        var homogeneous = true;
        var permanentLayerCount = 0;
        var prevailingType = "";
        var primaryLayerEditable = false;
        var layers = [];

        models.each(function (model) {
          if (
            !_.includes(layers, model.originalLayer) &&
            model.originalLayer != mapContext.getConfiguration().workingLayer
          ) {
            layers.push(model.originalLayer);

            if (model.originalLayer.isEditable()) {
              primaryLayerEditable = true;
            }
          }

          if (!prevailingType) {
            prevailingType = model.getFeature().getGeometry().get("type");
          } else {
            if (
              model.getFeature().getGeometry().get("type") != prevailingType
            ) {
              homogeneous = false;
            }
          }
        });

        return (
          homogeneous &&
          ((layers.length == 1 && primaryLayerEditable) ||
            permanentLayerCount == 0) &&
          prevailingType == "LineString"
        );
      } else {
        return false;
      }
    },
  }),

  new Operation({
    id: "split-geometry",
    text: "Split Feature",
    action: function (models) {
      var deferred = new $.Deferred();
      var geometryEngine = window.require("esri/geometry/geometryEngine");
      var cutter, toCut;

      models = normalizeModels(models);
      cutter = models.at(0);
      toCut = models.at(1);

      //we have a few cases in which we might want to reverse these
      //1. cutter is a polygon
      //2. cutter is on a saved layer

      if (
        cutter.get("type") == "Polygon" ||
        cutter.originalLayer != mapContext.getConfiguration().workingLayer
      ) {
        cutter = models.at(1);
        toCut = models.at(0);
      }

      cutter = cutter.getGeometry();
      toCut = toCut.getGeometry();

      // now, we need to
      // 1. get the esri-style geometry for everything
      // 2. do the cut operation
      // 3. create a new feature for one part of the split and update the geometry for the other feature that got split
      //     - need to update properties and geometry
      // 4. save the new feature(s)

      var generatedGeometries = [];

      try {
        // steps 1 and 2
        generatedGeometries = geometryEngine.cut(
          toCut.generateEsriGeometry(),
          cutter.generateEsriGeometry()
        );
      } catch (err) {
        deferred.reject(
          ErrorUtils.normalizeError(
            {
              activity: "splitting the feature",
            },
            err
          )
        );
      }

      if (generatedGeometries && generatedGeometries.length) {
        var layer = toCut.originalLayer;

        // step 1: remove the original geometry and retain its properties
        // step 2: split the geometry into bits
        // step 3: modify the original geometry to be one of the bits
        // step 4: for the remaining bits, create features with attributes from the original geometry
        // step 5: save everything
        // step 6: add all the bits back onto the layer

        var cutProperties = layer.remove(toCut);

        toCut.parseEsriGeometry(_.first(generatedGeometries));

        var preservedAttributes = _.clone(cutProperties.attributes);

        var newFeatures = _.map(
          _.rest(generatedGeometries),
          function (generatedGeometry) {
            var result;
            var geometry = new Geometry();

            geometry.parseEsriGeometry(generatedGeometry);
            result = Feature.generate(geometry, _.clone(preservedAttributes), {
              featureLayer: layer.featureLayer,
            });

            result.originalLayer = toCut.originalLayer;

            return result;
          }
        );

        var resultingFeatures = normalizeModels(
          [toCut.getFeature()].concat(newFeatures)
        );

        if (toCut.originalLayer != mapContext.getConfiguration().workingLayer) {
          mapContext
            .getOperation("commit-geometry")
            .action(resultingFeatures)
            .fail(function (err) {
              deferred.reject(err);
            })
            .done(function () {
              resultingFeatures.each(function (resultingFeature) {
                layer.add(
                  resultingFeature.getGeometry(),
                  resultingFeature.getProperties()
                );
              });

              mapContext.setSelection(resultingFeatures);
              mapContext.display(resultingFeatures);
              mapContext
                .getOperation("highlight-geometry")
                .action(resultingFeatures)
                .fail(function (err) {
                  // we don't really *need* to highlight the geometry, so emit this as a warning of sorts
                  console.error(err);
                })
                .always(function () {
                  deferred.resolve(
                    resultingFeatures,
                    "Feature split successfully."
                  );
                });
            });
        } else {
          resultingFeatures.each(function (resultingFeature) {
            layer.add(
              resultingFeature.getGeometry(),
              resultingFeature.getProperties()
            );
          });

          mapContext.setSelection(resultingFeatures);
          deferred.resolve(resultingFeatures, "Feature split successfully.");
        }
      }

      return deferred.promise();
    },

    available: function (models) {
      models = normalizeModels(models);
      if (hasEditableFeatures(models) && models.length == 2) {
        var lineCount = 0,
          polygonCount = 0,
          layersAreEditable = true,
          permanentLayerCount = 0;
        var type = "";

        models.each(function (model) {
          if (
            model.originalLayer != mapContext.getConfiguration().workingLayer
          ) {
            permanentLayerCount++;
          }

          if (!model.originalLayer.isEditable()) {
            layersAreEditable = false;
          }

          type = model.getFeature().getGeometry().get("type");

          if (type == "LineString") {
            lineCount++;
          } else if (type == "Polygon") {
            polygonCount++;
          }
        });

        return (
          layersAreEditable &&
          permanentLayerCount <= 1 &&
          lineCount > 0 &&
          lineCount + polygonCount == models.length
        );
      } else {
        return false;
      }
    },
  }),
]);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/operations/map/GeometryArrayOperations.js
// module id = 903
// module chunks = 1